import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import CloseIcon from '@mui/icons-material/Close';
import { VocabAutocomplete } from '../common/VocabAutocomplete';
import type { BeatDto } from '../../api/queries/storyArchitect';

const BEAT_TYPES = [
  'setup',
  'inciting_incident',
  'rising_action',
  'midpoint',
  'climax',
  'falling_action',
  'resolution',
  'custom',
] as const;

const EMOTIONAL_DIRECTIONS = [
  'rising',
  'steady',
  'falling',
  'peak',
  'shift',
] as const;

interface BeatDetailPanelProps {
  beat: BeatDto | null;
  open: boolean;
  onClose: () => void;
  onSave: (updates: {
    id: string;
    name: string;
    beatType?: string;
    description?: string;
    synopsis?: string | null;
    location?: string | null;
    timePeriod?: string | null;
    mood?: string | null;
    tone?: string | null;
    emotionalIntensity?: number | null;
    emotionalDirection?: string | null;
    notes?: string | null;
  }) => void;
  onDelete: (id: string) => void;
}

export function BeatDetailPanel({
  beat,
  open,
  onClose,
  onSave,
  onDelete,
}: BeatDetailPanelProps) {
  const [form, setForm] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (beat) {
      setForm({
        name: beat.name,
        beatType: beat.beatType ?? '',
        description: beat.description ?? '',
        synopsis: beat.synopsis ?? '',
        location: beat.location ?? '',
        timePeriod: beat.timePeriod ?? '',
        mood: beat.mood ?? '',
        tone: beat.tone ?? '',
        emotionalIntensity: beat.emotionalIntensity ?? 5,
        emotionalDirection: beat.emotionalDirection ?? '',
        notes: beat.notes ?? '',
      });
    }
  }, [beat]);

  if (!beat) return null;

  const set = (key: string, value: unknown) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    onSave({
      id: beat.id,
      name: (form.name as string) || beat.name,
      beatType: (form.beatType as string) || undefined,
      description: (form.description as string) || undefined,
      synopsis: (form.synopsis as string) || null,
      location: (form.location as string) || null,
      timePeriod: (form.timePeriod as string) || null,
      mood: (form.mood as string) || null,
      tone: (form.tone as string) || null,
      emotionalIntensity:
        form.emotionalIntensity != null
          ? Number(form.emotionalIntensity)
          : null,
      emotionalDirection: (form.emotionalDirection as string) || null,
      notes: (form.notes as string) || null,
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 480, p: 2 } }}
    >
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Beat Details
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Stack spacing={2} sx={{ overflow: 'auto', flex: 1, pb: 2 }}>
        <TextField
          label="Name"
          value={form.name ?? ''}
          onChange={(e) => set('name', e.target.value)}
          fullWidth
          required
        />

        <FilterableSelect
          options={BEAT_TYPES.map((t) => ({
            value: t,
            label: t
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (c) => c.toUpperCase()),
          }))}
          value={(form.beatType as string) ?? ''}
          onChange={(newValue) => set('beatType', newValue)}
          label="Beat Type"
          allowEmpty={true}
          emptyLabel="None"
        />

        <TextField
          label="Description"
          value={form.description ?? ''}
          onChange={(e) => set('description', e.target.value)}
          fullWidth
          multiline
          rows={2}
        />

        <TextField
          label="Synopsis"
          value={form.synopsis ?? ''}
          onChange={(e) => set('synopsis', e.target.value)}
          fullWidth
          multiline
          rows={3}
        />

        <Divider />
        <Typography variant="subtitle2" color="text.secondary">
          Setting
        </Typography>

        <Stack direction="row" spacing={1}>
          <TextField
            label="Location"
            value={form.location ?? ''}
            onChange={(e) => set('location', e.target.value)}
            fullWidth
          />
          <TextField
            label="Time Period"
            value={form.timePeriod ?? ''}
            onChange={(e) => set('timePeriod', e.target.value)}
            fullWidth
          />
        </Stack>

        <Stack direction="row" spacing={1}>
          <VocabAutocomplete
            kind="mood"
            value={(form.mood as string) || null}
            onChange={(v) => set('mood', v ?? '')}
            label="Mood"
          />
          <VocabAutocomplete
            kind="tone_modifiers"
            value={(form.tone as string) || null}
            onChange={(v) => set('tone', v ?? '')}
            label="Tone"
          />
        </Stack>

        <Divider />
        <Typography variant="subtitle2" color="text.secondary">
          Emotional Arc
        </Typography>

        <Box sx={{ px: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Emotional Intensity (0-10)
          </Typography>
          <Slider
            value={(form.emotionalIntensity as number) ?? 5}
            onChange={(_, v) => set('emotionalIntensity', v)}
            min={0}
            max={10}
            step={1}
            marks
            valueLabelDisplay="auto"
          />
        </Box>

        <FilterableSelect
          options={EMOTIONAL_DIRECTIONS.map((d) => ({
            value: d,
            label: d.charAt(0).toUpperCase() + d.slice(1),
          }))}
          value={(form.emotionalDirection as string) ?? ''}
          onChange={(newValue) => set('emotionalDirection', newValue)}
          label="Emotional Direction"
          allowEmpty={true}
          emptyLabel="None"
        />

        <Divider />

        <TextField
          label="Notes"
          value={form.notes ?? ''}
          onChange={(e) => set('notes', e.target.value)}
          fullWidth
          multiline
          rows={3}
          placeholder="Private notes..."
        />
      </Stack>

      <Divider sx={{ my: 1 }} />
      <Stack direction="row" spacing={1} justifyContent="space-between">
        <Button color="error" onClick={() => onDelete(beat.id)}>
          Delete Beat
        </Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </Stack>
    </Drawer>
  );
}
